// @flow
import React from 'react';
import { array, object } from 'yup';
import { toastError, toastSuccess } from '@presentational/notifications/utils';
import { Spacer } from '@presentational/spacing/Spacer';
import { streamGovernanceConfigArraySchema } from '@src/configuration/stream-governance';

import { validateClusters, createClustersFromBaseline } from './cluster-utils';
import { createFlinkPoolSchema, createFlinkPoolsFromBaseline } from './flink-utils';
import { reshapeSGForValidation, createStreamGovernanceFromBaseline } from './sg-utils';

export const DELAY_BETWEEN_MUTATIONS_MS = 10;

export const getValidationSchema = (numberOfMonths, metaData) =>
  object({
    flinkPools: array().of(createFlinkPoolSchema(numberOfMonths, metaData)).notRequired(),
    streamGovernance: streamGovernanceConfigArraySchema(metaData).notRequired(),
  });

const validateData = async (data, validationSchema) => {
  try {
    await validationSchema.validate(data, {
      abortEarly: true,
      strict: true,
    });
    return true;
  } catch (err) {
    showValidationError(err);
    return false;
  }
};

const showValidationError = (err) => {
  toastError(
    null,
    <div>
      Found this error in the uploaded file.
      <Spacer y={20} />
      Path: {err.path}
      <Spacer y={20} />
      Error: {err.message}
      <Spacer y={20} />
      Fix and try again!
      <Spacer y={20} />
    </div>,
    'top-left',
    false,
    true
  );
};

export const addMonthIndexToInputs = (inputs) =>
  inputs.map((input, index) => ({ ...input, month: index + 1 }));

// Clusters/Flink pools/SG in the file are priced against the estimate's current rate card, so
// importing a file built for a different rate card would silently misprice the estimate.
const validateFileRateCard = (jsonData, currentRateCardLabel) => {
  const fileRateCardLabel = jsonData.rate_card?.label;
  if (!fileRateCardLabel) {
    showValidationError({
      path: 'rate_card.label',
      message: 'The uploaded file is missing a rate_card.label. This field is required.',
    });
    return false;
  }

  if (fileRateCardLabel !== currentRateCardLabel) {
    showValidationError({
      path: 'rate_card.label',
      message: `The file's rate card ("${fileRateCardLabel}") does not match the estimate's current rate card ("${currentRateCardLabel}"). Update one to match and try again.`,
    });
    return false;
  }

  return true;
};

export const throwOnError = (error, context) => {
  if (error) {
    // eslint-disable-next-line no-console
    console.error(`Baseline import error at: ${context}`, error);
    toastError(error);
    throw new Error(`Baseline import failed at: ${context}`);
  }
};

export const processBaselineImport = async (
  jsonData,
  estimateId,
  estimateVersion,
  numberOfMonths,
  mutations,
  estimate,
  metaData,
  currentRateCardLabel
) => {
  if (!validateFileRateCard(jsonData, currentRateCardLabel)) {
    return;
  }

  // Validate clusters using the same config as the UI form
  if (jsonData.clusters && jsonData.clusters.length > 0) {
    try {
      await validateClusters(jsonData.clusters, estimate, metaData);
    } catch (err) {
      showValidationError(err);
      return;
    }
  }

  // Validate flink pools and stream governance
  const validationSchema = getValidationSchema(numberOfMonths, metaData);
  const isValid = await validateData(reshapeSGForValidation(jsonData), validationSchema);
  if (!isValid) {
    return;
  }

  let currentVersion = estimateVersion;

  if (jsonData.clusters && jsonData.clusters.length > 0) {
    currentVersion = await createClustersFromBaseline(
      jsonData.clusters,
      estimateId,
      currentVersion,
      mutations.addCluster,
      mutations.updateCluster,
      estimate
    );
  }

  if (jsonData.flinkPools && jsonData.flinkPools.length > 0) {
    currentVersion = await createFlinkPoolsFromBaseline(
      jsonData.flinkPools,
      estimateId,
      currentVersion,
      mutations.addFlinkPool,
      mutations.updateFlinkPool
    );
  }

  if (jsonData.streamGovernance && jsonData.streamGovernance.length > 0) {
    await createStreamGovernanceFromBaseline(
      jsonData.streamGovernance,
      estimateId,
      currentVersion,
      mutations.createStreamGovernanceConfig
    );
  }

  toastSuccess('Baseline imported successfully!');
};

export const formResetHandler = (formik, setOpen) => {
  formik.resetForm();
  setOpen(false);
};
